'use client'

// @ts-strict-ignore
import clsx from 'clsx'
import { ChangeEvent, Fragment, MouseEvent, MutableRefObject, ReactNode, useEffect, useRef, useState } from 'react'

import { useMediaQuery } from '@/shared/lib/hooks/useMediaQuery'
import useOutsideClick from '@/shared/lib/hooks/useOutsideClick'
import { Icon } from '@/shared/ui/icon'

import { LoadingCircle } from '../../../ui/LoadingCircle'
import { Option as OptionType, SelectorProps } from '../types/types'

import { Empty } from './Empty/Empty'
import { MobileSelect, MobileSelectTrigger } from './mobile-select/mobile-select'
import { Option } from './Option/Option'

import cl from './Select.module.scss'

export const Select = (props: SelectorProps) => {
	const {
		className,
		options = [],
		renderOption,
		value,
		defaultValue,
		onChange,
		disabled,
		adaptive,
		loading,
		placeholder,
		triggerLabel,
		mobileTitle,
		mobileModal = false,
		mobileCloseControl,
		error,
		isSearchable,
		isClear,
		hideIcon,
		children
	} = props

	const rootRef = useRef<HTMLDivElement>(null)
	const searchInputRef = useRef<HTMLInputElement>(null)
	const timerRef = useRef() as MutableRefObject<ReturnType<typeof setTimeout>>
	const isMobile = useMediaQuery('(max-width: 991px)')
	const isMobileModal = mobileModal && isMobile

	const [isOpen, setIsOpen] = useState(null)
	const [state, setState] = useState<'closing'>(null)
	const [selectedValue, setSelectedValue] = useState<OptionType>(defaultValue || null)
	const [searchValue, setSearchValue] = useState<string>(null)

	useEffect(() => {
		if (value !== undefined) {
			const foundOption = options.find(option => option?.value === value)
			setSelectedValue(foundOption ?? null)
		} else {
			setSelectedValue(null)
		}
	}, [value, options])

	const openSelect = () => {
		if (disabled) return
		setIsOpen(true)
	}

	const closeSelect = () => {
		if (isMobileModal) {
			setSearchValue('')
			setIsOpen(false)
			searchInputRef.current?.blur()
			return
		}

		setState('closing')

		timerRef.current = setTimeout(() => {
			setSearchValue('')
			setIsOpen(false)
			setState(null)
			searchInputRef.current?.blur()
		}, 300)
	}

	const handleClick = () => {
		if (isOpen) {
			closeSelect()
		} else {
			openSelect()
		}
	}

	const getDisplayedValue = () => {
		if (triggerLabel) return triggerLabel
		if (searchValue) return ''

		const optionWithSameValue = options.find(option => option.value === value)
		if (optionWithSameValue) return optionWithSameValue.label
		if (value) return value

		if (selectedValue) return selectedValue.label

		return placeholder
	}

	const getDisplayedOptions = () => {
		if (!searchValue) return options
		return options.filter(option => option.label.toLowerCase().indexOf(searchValue.toLowerCase()) >= 0)
	}

	const isSelected = (option: OptionType) => {
		if ((mobileModal || renderOption) && value !== undefined) return option.value === value
		if (!selectedValue) return false
		return selectedValue.value === option.value
	}

	const handleSelect = (option: OptionType) => {
		if ((mobileModal || renderOption) && option.value === (value ?? selectedValue?.value)) return

		setSelectedValue(option)
		onChange?.(option.value)
		closeSelect()
	}

	const onSearch = (e: ChangeEvent<HTMLInputElement>) => {
		setSearchValue(e.target.value)
	}

	const clearSearchField = (e: MouseEvent<SVGElement>) => {
		e.stopPropagation()
		setSearchValue('')
		searchInputRef.current?.focus()
	}

	const onClickArrowDown = () => {
		searchInputRef.current?.focus()
	}

	const onSearchIconClick = (e: MouseEvent<SVGElement>) => {
		e.stopPropagation()
		searchInputRef.current?.focus()
	}

	const optionsList = getDisplayedOptions().map(option =>
		renderOption ? (
			<Fragment key={option.value}>{renderOption(option, () => handleSelect(option))}</Fragment>
		) : (
			<Option
				key={option.value}
				data={option}
				onClick={handleSelect}
				isSelected={isSelected(option)}
				highlightText={mobileModal}
			/>
		)
	)

	let icon: ReactNode

	if (isOpen && isSearchable) {
		icon = searchValue ? (
			<Icon name="Close" size={14} className={cl.input_container__icon} onClick={clearSearchField} />
		) : (
			<Icon name="Search" size={14} className={cl.input_container__icon} onClick={onSearchIconClick} />
		)
	} else if (loading) {
		icon = <LoadingCircle size={12} />
	} else {
		icon = (
			<Icon
				name="ArrowDown"
				size={14}
				className={clsx(cl.input_container__icon, cl.input_container__arrow)}
				onClick={onClickArrowDown}
			/>
		)
	}

	const rootClasses = clsx(cl.root, className, {
		[cl.root__open]: isOpen,
		[cl.root__adaptive]: adaptive,
		[cl.root__disabled]: disabled,
		[cl.root__error]: error,
		[cl.root__searchable]: isSearchable,
		[cl.root__clear]: isClear,
		[cl.root__hideIcon]: hideIcon,
		[cl.root__mobileModal]: mobileModal
	})

	useOutsideClick(rootRef, () => {
		if (isOpen && !isMobileModal) closeSelect()
	})

	useEffect(() => {
		setSelectedValue(options.find(option => option?.value === defaultValue?.value) ?? null)
	}, [value]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		return () => {
			clearTimeout(timerRef.current)
		}
	}, [isOpen])

	return (
		<div
			className={rootClasses}
			data-state={state}
			ref={rootRef}
			tabIndex={isMobileModal ? -1 : 0}
			onClick={isMobileModal ? undefined : handleClick}>
			{isMobileModal ? (
				<MobileSelectTrigger label={getDisplayedValue()} onClick={openSelect} />
			) : (
				<div className={cl.input_container} tabIndex={-1}>
					{isSearchable && (
						<input
							ref={searchInputRef}
							className={cl.input_container__input}
							type="text"
							value={searchValue}
							onChange={onSearch}
						/>
					)}
					<span
						className={clsx(cl.input_container__value, {
							[cl.input_container__value__placeholder]:
								!triggerLabel && (getDisplayedValue() === placeholder || (isOpen && isSearchable))
						})}>
						{getDisplayedValue()}
					</span>
					{!hideIcon && icon}
					{children}
				</div>
			)}

			{isOpen && !isMobileModal && (
				<div
					className={cl.options}
					onClick={mobileModal || renderOption ? event => event.stopPropagation() : undefined}>
					<div className={clsx(cl.options__list, (mobileModal || renderOption) && cl._stable)}>
						{optionsList?.length > 0 ? optionsList : <Empty />}
					</div>
				</div>
			)}
			{isMobileModal && (
				<MobileSelect
					closeControl={mobileCloseControl}
					title={mobileTitle || placeholder || 'Выберите значение'}
					options={options}
					value={value ?? selectedValue?.value}
					isOpen={Boolean(isOpen)}
					onClose={closeSelect}
					onChange={nextValue => {
						if (Array.isArray(nextValue)) return

						const option = options.find(option => option.value === nextValue)
						if (option) handleSelect(option)
					}}
				/>
			)}
		</div>
	)
}
